import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SetupWizard — wizard de lancement du back-office.
 *
 * Permet à l'administrateur de configurer lui-même la boutique avant la mise en
 * ligne, sans intervention du développeur :
 *   1. Identité (branding + réseaux)        -> PUT /api/admin/settings
 *   2. Livraison & frais de port            -> PUT /api/admin/settings
 *   3. Paiements Stripe (secrets)           -> PUT /api/admin/setup/secrets (+ test)
 *   4. Emails (expéditeur)                   -> PUT /api/admin/settings
 *   5. Pages légales                         -> PUT /api/admin/settings
 *   6. Récapitulatif / état de préparation   -> GET /api/admin/setup/status
 *
 * Les secrets Stripe sont écrits dans AWS Secrets Manager via une route admin :
 * ils ne sont jamais renvoyés en clair (le statut affiche une version masquée).
 */

const inputClass =
  'w-full bg-transparent border border-black/15 focus:border-gold focus:outline-none ' +
  'rounded-none px-4 py-3 text-foreground placeholder:text-foreground/40 transition-colors';
const labelClass = 'block text-xs uppercase tracking-widest text-foreground/50 mb-2';

const STEPS = ['Identité', 'Livraison', 'Paiements', 'Emails', 'Légales', 'Récap'];

const SetupWizard = () => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);

  // Champs (un seul objet d'état).
  const [f, setF] = useState({
    // branding / social
    name: '',
    baseline: '',
    contact_email: '',
    phone: '',
    instagram: '',
    facebook: '',
    // shipping
    free_shipping_threshold: 120,
    flat_rate: 5.9,
    currency: 'eur',
    // stripe
    stripe_api_key: '',
    stripe_webhook_secret: '',
    // email
    email_from: '',
    // legal
    cgv: '',
    mentions: '',
    confidentialite: '',
  });

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  // Préremplissage depuis les settings publics existants + état.
  useEffect(() => {
    axios.get(`${API}/settings`).then((res) => {
      const s = res.data || {};
      setF((prev) => ({
        ...prev,
        name: s.branding?.name ?? prev.name,
        baseline: s.branding?.baseline ?? prev.baseline,
        contact_email: s.branding?.contact_email ?? prev.contact_email,
        phone: s.branding?.phone ?? prev.phone,
        instagram: s.social?.instagram ?? prev.instagram,
        facebook: s.social?.facebook ?? prev.facebook,
        free_shipping_threshold: s.shipping?.free_shipping_threshold ?? prev.free_shipping_threshold,
        flat_rate: s.shipping?.flat_rate ?? prev.flat_rate,
        currency: s.shipping?.currency ?? prev.currency,
        email_from: s.email?.from ?? prev.email_from,
        cgv: s.legal?.cgv ?? prev.cgv,
        mentions: s.legal?.mentions ?? prev.mentions,
        confidentialite: s.legal?.confidentialite ?? prev.confidentialite,
      }));
    }).catch(() => {});
    refreshStatus();
  }, []);

  const refreshStatus = () => {
    axios.get(`${API}/admin/setup/status`).then((r) => setStatus(r.data)).catch(() => {});
  };

  const saveSettings = async (payload, label) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, payload);
      toast.success(`${label} enregistré`);
      refreshStatus();
      return true;
    } catch (e) {
      toast.error(`Échec : ${e.response?.data?.detail || e.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveBranding = () =>
    saveSettings(
      {
        branding: { name: f.name, baseline: f.baseline, contact_email: f.contact_email, phone: f.phone },
        social: { instagram: f.instagram, facebook: f.facebook },
      },
      'Identité',
    );

  const saveShipping = () =>
    saveSettings(
      {
        shipping: {
          free_shipping_threshold: Number(f.free_shipping_threshold),
          flat_rate: Number(f.flat_rate),
          currency: f.currency,
        },
      },
      'Livraison',
    );

  const saveEmail = () => saveSettings({ email: { from: f.email_from } }, 'Email expéditeur');

  const saveLegal = () =>
    saveSettings({ legal: { cgv: f.cgv, mentions: f.mentions, confidentialite: f.confidentialite } }, 'Pages légales');

  const testStripe = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/setup/test/stripe`, {
        stripe_api_key: f.stripe_api_key || undefined,
      });
      if (r.data.ok) toast.success(`Clé Stripe valide (mode ${r.data.mode})`);
      else toast.error(r.data.detail || 'Clé Stripe invalide');
    } catch (e) {
      toast.error(`Échec test : ${e.response?.data?.detail || e.message}`);
    } finally {
      setTesting(false);
    }
  };

  const saveStripe = async () => {
    if (!f.stripe_api_key && !f.stripe_webhook_secret) {
      toast.error('Renseigne au moins une clé.');
      return false;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/setup/secrets`, {
        stripe_api_key: f.stripe_api_key || undefined,
        stripe_webhook_secret: f.stripe_webhook_secret || undefined,
      });
      toast.success('Secrets Stripe enregistrés (chiffrés)');
      set('stripe_api_key', '');
      set('stripe_webhook_secret', '');
      refreshStatus();
      return true;
    } catch (e) {
      toast.error(`Échec : ${e.response?.data?.detail || e.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Sauvegarde de l'étape courante avant d'avancer.
  const saveCurrent = async () => {
    switch (step) {
      case 0: return saveBranding();
      case 1: return saveShipping();
      case 2: return true; // Stripe a ses propres boutons
      case 3: return f.email_from ? saveEmail() : true;
      case 4: return saveLegal();
      default: return true;
    }
  };

  const next = async () => {
    const ok = await saveCurrent();
    if (ok !== false) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const ready = status?.ready;

  return (
    <div className="max-w-3xl">
      {/* Steps header */}
      <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2">
        {STEPS.map((label, i) => (
          <button
            key={label}
            onClick={() => setStep(i)}
            className="flex items-center gap-2 shrink-0"
            data-testid={`wizard-step-${i}`}
          >
            <span
              className={`flex items-center justify-center w-8 h-8 rounded-full text-xs border transition-colors ${
                i === step
                  ? 'bg-gold text-black border-gold'
                  : i < step
                    ? 'border-gold text-gold'
                    : 'border-black/20 text-foreground/40'
              }`}
            >
              {i < step ? <Check size={14} /> : i + 1}
            </span>
            <span className={`text-xs uppercase tracking-widest ${i === step ? 'text-gold' : 'text-foreground/40'}`}>
              {label}
            </span>
            {i < STEPS.length - 1 && <span className="w-6 h-px bg-black/15" />}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
          className="space-y-5"
        >
          {step === 0 && (
            <>
              <h3 className="font-heading text-2xl mb-4">Identité de la boutique</h3>
              <div>
                <label className={labelClass}>Nom de la boutique *</label>
                <input className={inputClass} value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Maison Ka Couture" data-testid="wizard-name" />
              </div>
              <div>
                <label className={labelClass}>Baseline</label>
                <input className={inputClass} value={f.baseline} onChange={(e) => set('baseline', e.target.value)} placeholder="L'héritage berbère réinventé" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Email de contact</label>
                  <input className={inputClass} value={f.contact_email} onChange={(e) => set('contact_email', e.target.value)} placeholder="contact@..." />
                </div>
                <div>
                  <label className={labelClass}>Téléphone</label>
                  <input className={inputClass} value={f.phone} onChange={(e) => set('phone', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Instagram</label>
                  <input className={inputClass} value={f.instagram} onChange={(e) => set('instagram', e.target.value)} placeholder="https://instagram.com/..." />
                </div>
                <div>
                  <label className={labelClass}>Facebook</label>
                  <input className={inputClass} value={f.facebook} onChange={(e) => set('facebook', e.target.value)} />
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h3 className="font-heading text-2xl mb-4">Livraison & frais de port</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Livraison offerte dès (€)</label>
                  <input type="number" className={inputClass} value={f.free_shipping_threshold} onChange={(e) => set('free_shipping_threshold', e.target.value)} data-testid="wizard-threshold" />
                </div>
                <div>
                  <label className={labelClass}>Frais de port (€)</label>
                  <input type="number" step="0.01" className={inputClass} value={f.flat_rate} onChange={(e) => set('flat_rate', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Devise</label>
                  <input className={inputClass} value={f.currency} onChange={(e) => set('currency', e.target.value)} />
                </div>
              </div>
              <p className="text-foreground/40 text-sm">France métropolitaine. La livraison est offerte automatiquement au-delà du seuil.</p>
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="font-heading text-2xl mb-2">Paiements Stripe</h3>
              <p className="text-foreground/50 text-sm mb-4 flex items-center gap-2">
                <ShieldCheck size={16} className="text-gold" />
                Tes clés sont chiffrées dans AWS Secrets Manager — jamais stockées en clair, jamais réaffichées.
              </p>
              {status?.stripe?.configured && (
                <p className="text-sm text-gold/80">Clé actuelle : {status.stripe.masked} (mode {status.stripe.mode})</p>
              )}
              <div>
                <label className={labelClass}>Clé secrète Stripe (sk_live_… ou sk_test_…)</label>
                <input className={inputClass} type="password" value={f.stripe_api_key} onChange={(e) => set('stripe_api_key', e.target.value)} placeholder="sk_live_..." data-testid="wizard-stripe-key" autoComplete="off" />
              </div>
              <div>
                <label className={labelClass}>Secret de signature webhook (whsec_…)</label>
                <input className={inputClass} type="password" value={f.stripe_webhook_secret} onChange={(e) => set('stripe_webhook_secret', e.target.value)} placeholder="whsec_..." autoComplete="off" />
                {status?.webhook?.configured && <p className="text-xs text-gold/70 mt-1">Webhook actuel : {status.webhook.masked}</p>}
              </div>
              <div className="flex gap-3 pt-2">
                <button className="btn-secondary inline-flex items-center gap-2" onClick={testStripe} disabled={testing} data-testid="wizard-stripe-test">
                  {testing && <Loader2 size={16} className="animate-spin" />} Tester la clé
                </button>
                <button className="btn-primary inline-flex items-center gap-2" onClick={saveStripe} disabled={saving} data-testid="wizard-stripe-save">
                  {saving && <Loader2 size={16} className="animate-spin" />} Enregistrer
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h3 className="font-heading text-2xl mb-4">Emails</h3>
              <div>
                <label className={labelClass}>Adresse expéditeur (vérifiée SES)</label>
                <input className={inputClass} value={f.email_from} onChange={(e) => set('email_from', e.target.value)} placeholder="contact@maisonka.couture" />
              </div>
              <p className="text-foreground/40 text-sm">L'adresse doit être une identité vérifiée dans AWS SES pour l'envoi des emails (confirmation de commande, etc.).</p>
            </>
          )}

          {step === 4 && (
            <>
              <h3 className="font-heading text-2xl mb-4">Pages légales</h3>
              {[
                ['cgv', 'CGV'],
                ['mentions', 'Mentions légales'],
                ['confidentialite', 'Confidentialité'],
              ].map(([k, lbl]) => (
                <div key={k}>
                  <label className={labelClass}>{lbl}</label>
                  <textarea className={`${inputClass} min-h-[100px]`} value={f[k]} onChange={(e) => set(k, e.target.value)} />
                </div>
              ))}
            </>
          )}

          {step === 5 && (
            <>
              <h3 className="font-heading text-2xl mb-4">Récapitulatif</h3>
              <div className="space-y-3">
                {status?.steps &&
                  Object.entries(status.steps).map(([key, s]) => (
                    <div key={key} className="flex items-center justify-between border border-black/10 px-4 py-3">
                      <span className="text-foreground/80">
                        {s.label} {s.required && <span className="text-gold/60 text-xs">(requis)</span>}
                      </span>
                      <span className={`text-xs uppercase tracking-widest flex items-center gap-1 ${s.done ? 'text-gold' : 'text-foreground/40'}`}>
                        {s.done ? (<><Check size={14} /> OK</>) : 'À faire'}
                      </span>
                    </div>
                  ))}
              </div>
              <div className={`mt-6 p-5 border ${ready ? 'border-gold/40 bg-gold/5' : 'border-black/10'}`} data-testid="wizard-ready">
                <p className={`font-heading text-xl ${ready ? 'text-gold' : 'text-foreground/70'}`}>
                  {ready ? '🎉 Boutique prête à être lancée' : 'Configuration incomplète'}
                </p>
                <p className="text-foreground/50 text-sm mt-1">
                  {ready
                    ? 'Tous les réglages requis sont en place. Tu peux passer Stripe en mode live et communiquer le site.'
                    : 'Complète les étapes marquées « requis » pour pouvoir lancer la boutique.'}
                </p>
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      <div className="flex items-center justify-between mt-10 pt-6 border-t border-black/10">
        <button onClick={prev} disabled={step === 0} className="inline-flex items-center gap-1 text-foreground/60 hover:text-gold disabled:opacity-30 transition-colors">
          <ChevronLeft size={18} /> Précédent
        </button>
        {step < STEPS.length - 1 ? (
          <button onClick={next} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="wizard-next">
            {saving && <Loader2 size={16} className="animate-spin" />} Suivant <ChevronRight size={16} />
          </button>
        ) : (
          <button onClick={refreshStatus} className="btn-secondary">Rafraîchir l'état</button>
        )}
      </div>
    </div>
  );
};

export default SetupWizard;
